import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import {
  App as AntApp,
  Button,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Space,
  Table,
  Typography,
} from 'antd';
import { useEffect, useState } from 'react';
import {
  useCategories,
  useCreateCategory,
  useDeleteCategory,
  useUpdateCategory,
} from '../api/hooks';
import type { Category } from '../api/types';
import { extractErrorMessage } from '../api/client';

const { Title } = Typography;

export default function CategoriesPage() {
  const { message } = AntApp.useApp();
  const list = useCategories();
  const create = useCreateCategory();
  const update = useUpdateCategory();
  const del = useDeleteCategory();

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Category | null>(null);
  const [form] = Form.useForm();

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ sort: 0 });
    setOpen(true);
  };

  const openEdit = (r: Category) => {
    setEditing(r);
    form.setFieldsValue({ name: r.name, description: r.description ?? '', sort: r.sort ?? 0 });
    setOpen(true);
  };

  const onSubmit = async () => {
    const v = await form.validateFields();
    const data = { name: v.name.trim(), description: v.description ?? '', sort: v.sort ?? 0 };
    try {
      if (editing) {
        await update.mutateAsync({ id: editing.id, data });
        message.success('Đã cập nhật danh mục');
      } else {
        await create.mutateAsync(data);
        message.success('Đã thêm danh mục');
      }
      setOpen(false);
    } catch (e) {
      message.error(extractErrorMessage(e));
    }
  };

  const onDelete = async (id: string) => {
    try {
      await del.mutateAsync(id);
      message.success('Đã xóa danh mục');
    } catch (e) {
      message.error(extractErrorMessage(e));
    }
  };

  const columns = [
    { title: 'Thứ tự', dataIndex: 'sort', key: 'sort', width: 90 },
    { title: 'Tên danh mục', dataIndex: 'name', key: 'name' },
    {
      title: 'Mô tả',
      dataIndex: 'description',
      key: 'description',
      render: (v: string) => v || '—',
    },
    {
      title: 'Thao tác',
      key: 'actions',
      width: 140,
      render: (_: unknown, r: Category) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)}>
            Sửa
          </Button>
          <Popconfirm
            title="Xóa danh mục này?"
            description="Các mục đang dùng tên danh mục này sẽ không bị ảnh hưởng (chỉ là chữ)."
            okText="Xóa"
            cancelText="Hủy"
            onConfirm={() => onDelete(r.id)}
          >
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 8 }}>
        <Title level={3} style={{ margin: 0 }}>
          Danh mục
        </Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
          Thêm danh mục
        </Button>
      </Space>
      <Typography.Paragraph type="secondary">
        Danh mục dùng chung cho <b>Nhà cung cấp</b>, <b>Model</b> và <b>Nhóm model</b>. Quản lý tại
        đây để các form chọn từ danh sách thay vì gõ tay (tránh sai chính tả, trùng lặp).
      </Typography.Paragraph>

      <Table<Category>
        rowKey="id"
        loading={list.isLoading}
        dataSource={list.data ?? []}
        columns={columns}
        pagination={false}
      />

      <Modal
        title={editing ? 'Sửa danh mục' : 'Thêm danh mục'}
        open={open}
        onCancel={() => setOpen(false)}
        onOk={onSubmit}
        okText="Lưu"
        cancelText="Hủy"
        confirmLoading={create.isPending || update.isPending}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="name"
            label="Tên danh mục"
            rules={[{ required: true, message: 'Vui lòng nhập tên danh mục' }]}
          >
            <Input placeholder="vd: Tạo ảnh, Tạo video, Chỉnh sửa ảnh" />
          </Form.Item>
          <Form.Item name="description" label="Mô tả">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item
            name="sort"
            label="Thứ tự hiển thị"
            tooltip="Số nhỏ hơn sẽ hiển thị lên trước."
          >
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
